import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpContext,
  HttpEvent,
  HttpEventType,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { Observable, map, switchMap, shareReplay, catchError, of } from 'rxjs';
import { RestApiService } from '../api/rest-api.service';

import {
  S3_UPLOAD_ENDPOINTS,
} from '../api/api-endpoints';
import { SKIP_AUTH } from '../interceptors/auth.interceptor';

export interface PresignedUploadRequest {
  file_name: string;
  content_type: string;
  size: number;
}

export interface PresignedUploadResponse {
  id?: string;
  upload_url: string;
  key: string;
  file_url: string;
}

export interface FileUrlResponse {
  key: string;
  file_url: string;
}

export interface UploadedFileResult {
  id: string;
  key: string;
  file_url: string;
}

export interface DeleteFileRequest {
  key: string;
  product_id?: string | null;
}

export interface DeleteFileResponse {
  key: string;
  deleted: boolean;
  detached_from_product?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class S3FileService {
  private fileUrlCache = new Map<string, Observable<string | null>>();
  private backendFileBlobUrlCache = new Map<string, Observable<string | null>>();

  constructor(
    private http: HttpClient,
    private restApi: RestApiService
  ) {}

  createPresignedUploadUrl(file: File): Observable<PresignedUploadResponse> {
    const payload: PresignedUploadRequest = {
      file_name: file.name,
      content_type: file.type || 'application/octet-stream',
      size: file.size,
    };

    return this.restApi.post<PresignedUploadResponse>(
      S3_UPLOAD_ENDPOINTS.upload_url,
      payload
    );
  }

  uploadToS3(uploadUrl: string, file: File): Observable<HttpEvent<string>> {
    const headers = new HttpHeaders({
      'Content-Type': file.type || 'application/octet-stream',
    });

    return this.http.put(uploadUrl, file, {
      headers,
      context: new HttpContext().set(SKIP_AUTH, true),
      observe: 'events',
      reportProgress: true,
      responseType: 'text',
    });
  }

  uploadFile(file: File): Observable<UploadedFileResult> {
    return this.createPresignedUploadUrl(file).pipe(
      switchMap((presignedResponse) =>
        this.uploadToS3(presignedResponse.upload_url, file).pipe(
          map((event) => {
            if (event.type === HttpEventType.Response) {
              return {
                id: presignedResponse.id ?? presignedResponse.key,
                key: presignedResponse.key,
                file_url: presignedResponse.file_url,
              };
            }

            return {
              id: '',
              key: '',
              file_url: '',
            };
          })
        )
      )
    );
  }

  getFileUrl(key: string): Observable<FileUrlResponse> {
    return this.restApi.get<FileUrlResponse>(
      S3_UPLOAD_ENDPOINTS.file_url,
      {
        params: new HttpParams().set('key', key),
      }
    );
  }

  deleteFile(payload: DeleteFileRequest): Observable<DeleteFileResponse> {
    return this.restApi.post<DeleteFileResponse>(
      S3_UPLOAD_ENDPOINTS.delete_file,
      payload
    );
  }

  getFileUrlValue(key?: string | null): Observable<string | null> {
    if (!key) {
      return of(null);
    }

    if (key.startsWith('http://') || key.startsWith('https://')) {
      return of(key);
    }

    const cached = this.fileUrlCache.get(key);

    if (cached) {
      return cached;
    }

    const url$ = this.getFileUrl(key).pipe(
      map((response) => response.file_url),
      catchError(() => of(null)),
      shareReplay(1)
    );

    this.fileUrlCache.set(key, url$);

    return url$;
  }

  getUploadProgress(event: HttpEvent<string>): number | null {
    if (event.type !== HttpEventType.UploadProgress || !event.total) {
      return null;
    }

    return Math.round((event.loaded / event.total) * 100);
  }

  getBackendFileUrl(keyOrUrl?: string | null): string | null {
  if (!keyOrUrl) {
    return null;
  }

  if (keyOrUrl.startsWith('http://') || keyOrUrl.startsWith('https://')) {
    return keyOrUrl;
  }

  const normalizedKey = keyOrUrl.replace(/^\/+/, '');

  // Adjust this base URL if you store it in environment.ts.
  const backendBaseUrl = 'http://localhost:8080';

  if (normalizedKey.startsWith('uploads/')) {
    return `${backendBaseUrl}/${normalizedKey}`;
  }

  return `${backendBaseUrl}/uploads/${normalizedKey}`;
}

getBackendFileBlobUrl(keyOrUrl?: string | null): Observable<string | null> {
  if (!keyOrUrl) {
    return of(null);
  }

  const cached = this.backendFileBlobUrlCache.get(keyOrUrl);

  if (cached) {
    return cached;
  }

  const fileUrl = this.getBackendFileUrl(keyOrUrl);

  if (!fileUrl) {
    return of(null);
  }

  const blobUrl$ = this.http
    .get(fileUrl, {
      responseType: 'blob',
      // Do not use SKIP_AUTH here unless the route is definitely public.
      // If /uploads/... is protected, your normal auth interceptor should attach the Bearer token.
    })
    .pipe(
      map((blob) => {
        if (!blob || blob.size === 0) {
          return null;
        }

        return URL.createObjectURL(blob);
      }),
      catchError(() => of(null)),
      shareReplay(1)
    );

  this.backendFileBlobUrlCache.set(keyOrUrl, blobUrl$);

  return blobUrl$;
}
}
